import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Text, Loader, Box } from '@mantine/core';
import { Alert } from '@xplatform/design-system';
import type { IExtractedNodeViewModel } from '@src/models';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './ImageDisplay.scss';

// Set up the worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

interface ImageDisplayProps {
    imageUrl?: string | null;
    isPdf?: boolean;
    currentPage: number;
    selectedNode?: IExtractedNodeViewModel | null;
}

const ImageDisplay: React.FC<ImageDisplayProps> = ({
    imageUrl,
    isPdf,
    currentPage,
    selectedNode,
}) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
    const [pdfPageDimensions, setPdfPageDimensions] = useState<{ width: number; height: number } | null>(null);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const pageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsImageLoaded(false);
        setImageDimensions(null);
        setPdfPageDimensions(null);
        setPdfError(null);
    }, [imageUrl, currentPage]);

    const handleImageLoad = () => {
        if (imageRef.current) {
            const { naturalWidth, naturalHeight } = imageRef.current;
            setImageDimensions({ width: naturalWidth, height: naturalHeight });
            setIsImageLoaded(true);
        }
    };

    const handlePdfLoadSuccess = () => {
        setIsImageLoaded(true);
        setPdfError(null);
    };

    const handlePdfLoadError = (error: Error) => {
        console.error('PDF load error in ImageDisplay:', error);
        let errorMessage = 'Failed to load PDF document.';

        if (error.message.includes('CORS')) {
            errorMessage = 'Cross-origin request blocked. Please check file permissions.';
        } else if (error.message.includes('Invalid PDF')) {
            errorMessage = 'The file appears to be corrupted or is not a valid PDF.';
        } else if (error.message.includes('password')) {
            errorMessage = 'This PDF is password-protected and cannot be displayed.';
        } else if (error.message) {
            errorMessage = error.message;
        }

        setPdfError(errorMessage);
    };

    const handlePageRenderSuccess = (page: { width: number; height: number }) => {
        const { width, height } = page;
        setPdfPageDimensions({ width, height });
        setIsImageLoaded(true);
    };

    const renderBoundingBox = () => {
        if (!selectedNode?.data?.bbox || !isImageLoaded) {
            return null;
        }

        if (selectedNode.data.page !== currentPage) {
            return null;
        }

        const bbox = selectedNode.data.bbox;
        let pageWidth: number;
        let pageHeight: number;
        let displayedWidth: number;
        let displayedHeight: number;

        if (isPdf && pageRef.current && pdfPageDimensions) {
            pageWidth = pdfPageDimensions.width;
            pageHeight = pdfPageDimensions.height;
            displayedWidth = pageRef.current.offsetWidth;
            displayedHeight = pageRef.current.offsetHeight;
        } else if (imageDimensions && imageRef.current) {
            pageWidth = imageDimensions.width;
            pageHeight = imageDimensions.height;
            displayedWidth = imageRef.current.offsetWidth;
            displayedHeight = imageRef.current.offsetHeight;
        } else {
            return null;
        }

        const { left, top, right, bottom } = bbox;
        const width = right - left;
        const height = bottom - top;

        const naturalLeft = left * pageWidth;
        const naturalTop = top * pageHeight;
        const naturalWidth = width * pageWidth;
        const naturalHeight = height * pageHeight;

        const scaleX = displayedWidth / pageWidth;
        const scaleY = displayedHeight / pageHeight;

        const scaledLeft = naturalLeft * scaleX;
        const scaledTop = naturalTop * scaleY;
        const scaledWidth = naturalWidth * scaleX;
        const scaledHeight = naturalHeight * scaleY;

        return (
            <div
                className="bounding-box"
                style={{
                    left: `${scaledLeft}px`,
                    top: `${scaledTop}px`,
                    width: `${scaledWidth}px`,
                    height: `${scaledHeight}px`,
                }}
            >
                {selectedNode.data.key && (
                    <div className="bounding-box__label">
                        conf: {selectedNode.data.conf}
                    </div>
                )}
            </div>
        );
    };

    if (!imageUrl) {
        return null;
    }

    if (isPdf) {
        if (pdfError) {
            return (
                <Box p="md" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <Alert color="red" maw={500}>
                        <Text size="sm" fw={600} mb="xs">
                            PDF Loading Error
                        </Text>
                        <Text size="sm">
                            {pdfError}
                        </Text>
                    </Alert>
                </Box>
            );
        }

        return (
            <div style={{ position: 'relative', display: 'inline-block' }} ref={pageRef}>
                <Document
                    file={imageUrl}
                    onLoadSuccess={handlePdfLoadSuccess}
                    onLoadError={handlePdfLoadError}
                    loading={
                        <Box p="md" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                            <Loader size={40} />
                        </Box>
                    }
                    error={
                        <Box p="md">
                            <Alert color="red">
                                <Text size="sm">
                                    Failed to load PDF. Please try again or select a different file.
                                </Text>
                            </Alert>
                        </Box>
                    }
                >
                    <Page
                        pageNumber={currentPage}
                        onRenderSuccess={handlePageRenderSuccess}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                    />
                </Document>
                {renderBoundingBox()}
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <img
                ref={imageRef}
                src={imageUrl}
                alt={`Document - Page ${currentPage}`}
                draggable={false}
                onLoad={handleImageLoad}
                className="document-image"
                style={{
                    opacity: isImageLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out'
                }}
            />
            {renderBoundingBox()}
        </div>
    );
};

export default ImageDisplay;
